import React from "react";
import { Link } from "react-router-dom";
import useReveal from "../hooks/useReveal";
import useDocMeta from "../hooks/useDocMeta";
import ContactSection from "../components/ContactSection";
import { ABOUT, META } from "../mock";

function AboutCard({ s }) {
  return (
    <div className="reveal flex flex-col bg-white rounded-[1.75rem] overflow-hidden border border-brand-100 shadow-[0_24px_60px_-45px_rgba(31,56,53,0.5)]">
      <div className="aspect-[16/10] overflow-hidden">
        <img src={s.image} alt={s.heading} className="h-full w-full object-cover" />
      </div>
      <div className="p-7 flex-1">
        <h3 className="font-display text-xl sm:text-2xl text-brand-800 mb-3 lg:min-h-[3.75rem]">{s.heading}</h3>
        <div className="space-y-3">
          {s.paragraphs.map((p, j) => (
            <p key={j} className="text-brand-900/75 leading-relaxed text-[0.98rem]">
              {p}
              {s.boldLine && j === s.paragraphs.length - 1 && (
                <> <strong className="font-semibold text-brand-900" data-testid="about-bold-line">{s.boldLine}</strong></>
              )}
              {s.linkLine && j === s.paragraphs.length - 1 && (
                <>
                  {" "}{s.linkLine.before}
                  <Link to={s.linkLine.to} data-testid="about-team-link" className="font-semibold text-brand-600 underline underline-offset-4 decoration-brand-300 hover:text-brand-700 hover:decoration-brand-600 transition-colors">
                    {s.linkLine.label}
                  </Link>
                  {s.linkLine.after}
                </>
              )}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function About() {
  useReveal();
  useDocMeta(META.about);
  const firstRow = ABOUT.sections.slice(0, 3);
  const secondRow = ABOUT.sections.slice(3);

  return (
    <div>
      <section className="relative pt-36 pb-14 sm:pt-44 sm:pb-16 bg-gradient-to-b from-brand-50 to-sand-50 overflow-hidden">
        <div className="absolute -top-24 right-0 h-80 w-80 rounded-full bg-brand-200/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-brand-500 font-semibold tracking-[0.2em] text-xs uppercase">AwareHub Mental Health Clinic</span>

          {/* Connected media block: image + intro as one unit */}
          <div className="mt-6 reveal rounded-[2rem] overflow-hidden bg-white border border-brand-100 shadow-[0_40px_90px_-50px_rgba(31,56,53,0.5)] grid lg:grid-cols-2">
            <div className="min-h-[280px] lg:min-h-full">
              <img src={ABOUT.heroImage} alt={ABOUT.title} className="h-full w-full object-cover" />
            </div>
            <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
              <h1 className="font-display text-3xl sm:text-4xl text-brand-900 leading-tight text-balance">
                {ABOUT.title}
              </h1>
              {ABOUT.intro && (
                <p className="mt-5 text-brand-900/75 leading-relaxed">{ABOUT.intro}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {firstRow.map((s, i) => (
              <AboutCard key={i} s={s} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-brand-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 lg:max-w-4xl lg:mx-auto">
            {secondRow.map((s, i) => (
              <AboutCard key={i} s={s} />
            ))}
          </div>
        </div>
      </section>

      <ContactSection />
    </div>
  );
}
